/**
 * Error Action Mapping System
 *
 * Maps error codes to specific recovery actions with user-friendly messaging
 * and appropriate recovery functions. Integrates with the loading context
 * and provides contextual error recovery options.
 *
 * @module ErrorActionMapping
 */

import { ERROR_CODES } from './AuthErrors'

/**
 * Action types for error recovery - core actions for different error scenarios
 */
export const ACTION_TYPES = {
    RETRY: 'RETRY',
    SETUP_ORGANIZATION: 'SETUP_ORGANIZATION',
    NAVIGATE_TO_WELCOME: 'NAVIGATE_TO_WELCOME',
    CONTACT_SUPPORT: 'CONTACT_SUPPORT',
    LOGOUT: 'LOGOUT',
}

/**
 * Action configurations for different error recovery strategies
 *
 * Maps core actions to their UI configurations:
 * - RETRY: Page reload for recoverable errors
 * - SETUP_ORGANIZATION: For organization setup related errors
 * - CONTACT_SUPPORT: For non-recoverable errors or after retry limit
 * - LOGOUT: For authentication errors that require re-authentication
 */
export const ACTION_CONFIGS = {
    [ACTION_TYPES.RETRY]: {
        buttonText: 'Retry',
        variant: 'contained',
        color: 'primary',
    },
    [ACTION_TYPES.SETUP_ORGANIZATION]: {
        buttonText: 'Set Up Organization',
        variant: 'contained',
        color: 'secondary',
    },
    [ACTION_TYPES.NAVIGATE_TO_WELCOME]: {
        buttonText: 'Go to Welcome',
        variant: 'contained',
        color: 'secondary',
    },
    [ACTION_TYPES.CONTACT_SUPPORT]: {
        buttonText: 'Contact Support',
        variant: 'outlined',
        color: 'error',
    },
    [ACTION_TYPES.LOGOUT]: {
        buttonText: 'Sign Out',
        variant: 'contained',
        color: 'warning',
    },
}

/**
 * Error code to action type mapping for different error scenarios
 */
export const ERROR_ACTION_MAP = {
    // Organization setup errors - specific actions
    [ERROR_CODES.NO_ORGANISATION]: ACTION_TYPES.SETUP_ORGANIZATION,
    [ERROR_CODES.NO_USER_ORGANISATION]: ACTION_TYPES.SETUP_ORGANIZATION,

    // Access denied - contact support immediately
    [ERROR_CODES.ORGANISATION_ACCESS_DENIED]: ACTION_TYPES.CONTACT_SUPPORT,

    // Authentication errors - logout and re-authenticate
    [ERROR_CODES.AUTHENTICATION_ERROR]: ACTION_TYPES.LOGOUT,
    [ERROR_CODES.MSAL_INITIALIZATION_FAILED]: ACTION_TYPES.LOGOUT,
    [ERROR_CODES.TOKEN_REFRESH_FAILED]: ACTION_TYPES.LOGOUT,

    // Other recoverable errors - retry with counter logic
    [ERROR_CODES.USER_NOT_FOUND]: ACTION_TYPES.RETRY,
    [ERROR_CODES.USER_CREATION_FAILED]: ACTION_TYPES.RETRY,
    [ERROR_CODES.USER_CONTEXT_INVALID]: ACTION_TYPES.RETRY,
    [ERROR_CODES.ORGANISATION_LOAD_FAILED]: ACTION_TYPES.RETRY,
    [ERROR_CODES.NETWORK_ERROR]: ACTION_TYPES.RETRY,
    [ERROR_CODES.UNKNOWN_ERROR]: ACTION_TYPES.RETRY,
}

/**
 * Retry counter storage key
 */
const RETRY_COUNTER_KEY = 'quotemate_error_retry_count'

/**
 * Get current retry count from session storage
 *
 * @returns {number} Current retry count
 */
export const getRetryCount = () => {
    try {
        const count = sessionStorage.getItem(RETRY_COUNTER_KEY)
        return count ? parseInt(count, 10) : 0
    } catch {
        return 0
    }
}

/**
 * Increment retry count in session storage
 *
 * @returns {number} New retry count
 */
export const incrementRetryCount = () => {
    try {
        const currentCount = getRetryCount()
        const newCount = currentCount + 1
        sessionStorage.setItem(RETRY_COUNTER_KEY, newCount.toString())
        return newCount
    } catch {
        return 1
    }
}

/**
 * Reset retry count in session storage
 */
export const resetRetryCount = () => {
    try {
        sessionStorage.removeItem(RETRY_COUNTER_KEY)
    } catch {
        // Silent fail - not critical
    }
}

/**
 * Get action configuration for a given error object with retry counter logic
 *
 * @param {Object} errorObject - The AuthenticationError object
 * @param {number} [retryCount] - Current retry count (optional, will fetch from storage if not provided)
 * @returns {Object|null} Action configuration or null if no action available
 */
export const getErrorAction = (errorObject, retryCount = null) => {
    if (!errorObject) {
        return null
    }

    // Get retry count from storage if not provided
    const currentRetryCount = retryCount !== null ? retryCount : getRetryCount()

    // If we've exceeded retry limit (2 attempts), force contact support
    if (currentRetryCount >= 2) {
        return {
            actionType: ACTION_TYPES.CONTACT_SUPPORT,
            ...ACTION_CONFIGS[ACTION_TYPES.CONTACT_SUPPORT],
        }
    }

    // Get base action from error code
    const actionType = errorObject.code ? ERROR_ACTION_MAP[errorObject.code] : ACTION_TYPES.RETRY

    // Check if error is non-recoverable - force contact support except for org setup
    if (errorObject.recoverable === false) {
        if (actionType === ACTION_TYPES.SETUP_ORGANIZATION) {
            // Allow org setup and welcome navigation even if marked non-recoverable
            return {
                actionType,
                ...ACTION_CONFIGS[actionType],
            }
        }
        // For other non-recoverable errors, show contact support
        return {
            actionType: ACTION_TYPES.CONTACT_SUPPORT,
            ...ACTION_CONFIGS[ACTION_TYPES.CONTACT_SUPPORT],
        }
    }

    // Return action configuration
    return {
        actionType,
        ...ACTION_CONFIGS[actionType],
    }
}

/**
 * Determine if an error should show an action button
 *
 * @param {Object} errorObject - The error object from loading context
 * @returns {boolean} Whether to show action button
 */
export const shouldShowAction = (errorObject) => {
    // Always show actions for recoverable errors
    if (errorObject?.recoverable !== false) {
        return true
    }

    // Show setup action for organization errors even if not recoverable
    if (errorObject?.code === ERROR_CODES.NO_USER_ORGANISATION || errorObject?.code === ERROR_CODES.NO_ORGANISATION) {
        return true
    }

    // Show contact support for other non-recoverable errors
    return true
}
